import React from 'react';

// Reusable Card component with updated styling
const AppCard = ({ name, description, bestFor, minInvestment, bonus, pros, cons, category }) => (
  <div style={styles.card}>
    <h3 style={styles.cardTitle}>{name}</h3>
    <p style={styles.cardDescription}><strong>Why it’s hot:</strong> {description}</p>
    <p><strong>Best for:</strong> {bestFor}</p>
    <p><strong>Minimum investment:</strong> ${minInvestment}</p>
    <p><strong>Bonus:</strong> {bonus}</p>

    <h4 style={styles.subTitle}>Category: {category}</h4>
    <div style={styles.contentSection}>
      <h5>Advantages:</h5>
      <ul style={styles.list}>
        {pros.map((pro, index) => (
          <li key={index}>{pro}</li>
        ))}
      </ul>
    </div>
    <div style={styles.contentSection}>
      <h5>Disadvantages:</h5>
      <ul style={styles.list}>
        {cons.map((con, index) => (
          <li key={index}>{con}</li>
        ))}
      </ul>
    </div>
  </div>
);

// Main article component
const InvestingApp = () => {
  const apps = [
    {
      name: "Robinhood",
      description: "Effortless, commission-free trading with real-time data and a sleek interface.",
      bestFor: "Beginners, Stock Traders, and Crypto Enthusiasts",
      minInvestment: 1,
      bonus: "Get a free stock when you sign up!",
      pros: [
        "No commission fees on trades.",
        "User-friendly interface, great for beginners.",
        "Access to stocks, options, and crypto all in one place."
      ],
      cons: [
        "Limited advanced trading tools for experienced traders.",
        "Customer service can be slow at times.",
        "Limited research tools compared to other platforms."
      ],
      category: "Beginner Traders & Casual Investors"
    },
    {
      name: "Acorns",
      description: "Automated micro-investing by rounding up purchases and investing your spare change.",
      bestFor: "Passive Investors and Long-term Savers",
      minInvestment: 5,
      bonus: "$10 bonus when you open an account",
      pros: [
        "Automated investing with round-up purchases.",
        "Perfect for people who don't want to think about investing.",
        "Good for long-term investors looking for slow, steady growth."
      ],
      cons: [
        "Limited control over specific investments.",
        "Fees can add up, especially with smaller balances.",
        "Not suitable for active traders."
      ],
      category: "Slow and Steady Investors"
    },
    {
      name: "eToro",
      description: "Social trading platform with a focus on both crypto and stocks, plus automatic copy-trading.",
      bestFor: "Crypto Enthusiasts and Hands-Off Investors",
      minInvestment: 10,
      bonus: "Free demo account for risk-free trading practice",
      pros: [
        "Social trading features allow you to copy successful traders.",
        "Wide range of crypto assets available for trading.",
        "Demo account to practice trading risk-free."
      ],
      cons: [
        "Higher fees for certain assets and withdrawals.",
        "Limited research tools compared to dedicated brokers.",
        "Withdrawal fees are not as competitive."
      ],
      category: "Crypto Traders & Hands-Off Investors"
    },
    {
      name: "Webull",
      description: "Advanced trading tools with zero commission on trades and real-time market data.",
      bestFor: "Active Traders and Advanced Investors",
      minInvestment: 0,
      bonus: "Up to 12 free stocks for new users",
      pros: [
        "Zero commissions on trades, including options.",
        "Advanced charting and real-time market data.",
        "Good for options traders and day traders."
      ],
      cons: [
        "Not as beginner-friendly as other apps.",
        "Limited investment choices compared to larger platforms.",
        "No fractional shares for ETFs."
      ],
      category: "Active Traders & Options Traders"
    },
    {
      name: "Stash",
      description: "A personalized investment portfolio with automatic rebalancing and educational resources.",
      bestFor: "Beginner Investors Looking for Long-term Growth",
      minInvestment: 5,
      bonus: "$5 bonus with your first deposit",
      pros: [
        "Personalized portfolios and educational tools.",
        "Automatic rebalancing to maintain your asset allocation.",
        "Good for beginners who want to learn while they invest."
      ],
      cons: [
        "Fees can be high for small accounts.",
        "Limited control over specific stock and ETF choices.",
        "Not ideal for active traders or investors who want more flexibility."
      ],
      category: "Long-term Investors & ETF Traders"
    }
  ];

  return (
    <>
      <p style={styles.intro}>
        💸 Ready to make your money work for you in 2025? Check out these investment apps, each offering unique features to match different financial goals. Here's a breakdown of the pros and cons to help you choose wisely.
      </p>
      <div style={styles.container}>
        {apps.map((app, index) => (
          <AppCard key={index} {...app} />
        ))}
        <div style={styles.footer}>
          <h2 style={styles.footerTitle}>🚀 Your Next Step Toward Financial Freedom</h2>
          <p style={styles.footerText}>
            Remember, there’s no "one-size-fits-all" approach to investing. Whether you’re a <b>hands-off investor</b> or a <b>day trader</b>, choose the platform that best aligns with your goals, risk tolerance, and investing style.
          </p>
        </div>
      </div>
    </>
  );
};

const styles = {
  container: {
    maxWidth: '900px',
    margin: '0 auto',
    padding: '20px',
    fontFamily: 'Arial, sans-serif',
    backgroundColor: '#f9fafc',
    borderRadius: '12px',
    boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.05)',
    overflow: 'hidden',
  },
  intro: {
    fontSize: '1.1rem',
    marginBottom: '30px',
    color: '#333',
    lineHeight: '1.6',
    textAlign: 'center',
    fontWeight: '300',
  },
  card: {
    borderRadius: '12px',
    padding: '25px',
    marginBottom: '30px',
    background: 'linear-gradient(135deg, #0077B6, #00B4D8)',
    boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.1)',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease, background 0.3s ease',
    cursor: 'pointer',
    ':hover': {
      transform: 'scale(1.05)',
      boxShadow: '0px 12px 40px rgba(0, 0, 0, 0.1)',
      background: 'linear-gradient(135deg, #00B4D8, #0077B6)', // Reverse gradient on hover
    },
  },
  cardTitle: {
    fontSize: '1.8rem',
    color: '#fff',
    marginBottom: '15px',
    fontWeight: '600',
  },
  cardDescription: {
    fontSize: '1rem',
    color: '#fff',
    marginBottom: '20px',
  },
  subTitle: {
    fontSize: '1.2rem',
    color: '#fff',
    marginTop: '20px',
    fontWeight: '500',
  },
  list: {
    fontSize: '1rem',
    marginLeft: '20px',
    color: '#ddd',
    listStyleType: 'none',
  },
  contentSection: {
    marginTop: '20px',
  },
  footer: {
    marginTop: '60px',
    textAlign: 'center',
    padding: '30px',
    backgroundColor: '#003C5D', // Darker blue for footer
    color: 'white',
    borderRadius: '12px',
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
  },
  footerTitle: {
    fontSize: '1.8rem',
    color: '#fff',
    fontWeight: '500',
    marginBottom: '15px',
  },
  footerText: {
    fontSize: '1.2rem',
    marginBottom: '20px',
    lineHeight: '1.6',
  },
};

export default InvestingApp;
